import React, { useCallback, useMemo, useState } from 'react'

export default function useEmrSectionsData({ emrSections }) {
  const [emrSectionDataList, setEmrSectionDataList] = useState([])

  /**
   * Generic utility: Get EMR section data by section ID
   * @param {string} sectionId - The section ID (e.g., 'patient-history-notes', 'social-habits', etc.)
   * @returns {Object|undefined} The EMR section data for the given section ID
   */
  const getEmrSectionDataFromSectionId = useCallback(
    (sectionId) => {
      const emrSection = emrSections?.find((section) => section?.id === sectionId)
      if (!emrSection) return undefined

      const sectionCode = emrSection?.emrSectionResponse?.section?.defCode
      const emrData = emrSectionDataList?.find(
        (sectionData) => sectionData?.sectionCode === sectionCode,
      )
      return emrData
    },
    [emrSectionDataList, emrSections],
  )

  /**
   * Generic utility: Set/update EMR section data by section ID
   * @param {string} sectionId - The section ID (e.g., 'patient-history-notes', 'social-habits', etc.)
   * @param {Object|Function} newDataOrUpdater - New data object or updater function
   */
  const setSingleEmrSectionByEmrSectionId = useCallback(
    (sectionId, newDataOrUpdater) => {
      setEmrSectionDataList((prev) => {
        const emrSection = emrSections?.find((section) => section?.id === sectionId)
        if (!emrSection) {
          console.warn(`[useEmrSectionsData] Section ID "${sectionId}" not found in emrSections`)
          return prev
        }

        const sectionCode = emrSection?.emrSectionResponse?.section?.defCode
        let currentData = prev?.find((sectionData) => sectionData?.sectionCode === sectionCode)

        // Handle functional updates
        let updatedData
        if (typeof newDataOrUpdater === 'function') {
          updatedData = newDataOrUpdater(currentData)
        } else {
          updatedData = newDataOrUpdater
        }

        // Ensure sectionCode is set
        if (updatedData && !updatedData.sectionCode) {
          updatedData = { ...updatedData, sectionCode }
        }

        let finalArray = prev || []
        const existingIndex = finalArray.findIndex(
          (sectionData) => sectionData?.sectionCode === sectionCode,
        )

        if (existingIndex !== -1) {
          // Update existing section
          finalArray = finalArray.map((item, index) =>
            index === existingIndex ? updatedData : item,
          )
        } else {
          // Add new section
          finalArray = [...finalArray, updatedData]
        }

        return finalArray
      })
    },
    [emrSections],
  )

  // Backward compatibility: Keep existing patientHistoryEmrData and setPatientHistoryEmrData
  const patientHistoryEmrData = useMemo(() => {
    return getEmrSectionDataFromSectionId('patient-history-notes')
  }, [getEmrSectionDataFromSectionId])

  const setPatientHistoryEmrData = useCallback(
    (newDataOrUpdater) => {
      setSingleEmrSectionByEmrSectionId('patient-history-notes', newDataOrUpdater)
    },
    [setSingleEmrSectionByEmrSectionId],
  )

  // console.log({emrSectionDataList}) // Removed to prevent loop logging

  return {
    emrSectionDataList,
    setEmrSectionDataList,
    // Generic utilities (NEW)
    getEmrSectionDataFromSectionId,
    setSingleEmrSectionByEmrSectionId,
    // Backward compatibility
    patientHistoryEmrData,
    setPatientHistoryEmrData,
  }
}
